var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { convertToNumber3, convertToNumber4, degToRad, LayoutCreationFinishedMessage, MulticastDelegate, MulticastDelegate1, RoomChangedMessage, ROOT_MODEL_UID, StartPointFacet, StartPointFlags, LocalTransformFacet, } from "flavr-common";
import { Vector3 } from "flavr-math";
import { busService } from "../../bus";
import { ROOT_SCALE } from "../rootScale";
import { renderManager } from "../../xr/RenderManager";
import { localPlayerContainer } from "../../localplayer/LocalPlayerContainer";
import { SCALE_DEFAULT } from "../../localplayer/LocalPlayerModule";
import { SessionConnectMessage, SpawnPointsInRoomRequestMessage, SpawnPointsInRoomResponseMessage, UserSpawnedMessage, UserSpawnMessage } from "@messages/session/SessionMessages";
import { ScriptsCreationFinished } from "@messages/scripting/ScriptMessages";
import { RendererSystem } from "../renderer/RendererSystem";
import { camera } from "../camera";
import { SetPlayerWorldTransformMessage } from "@messages/locomotion/LocomotionMessages";
import { modelPool, determineModelNameForPrinting, modelToAbsolutePath } from "@modules/model";
import { RotatingRenderable } from "@modules/rotatingMesh/RotatingRenderable";
import { roomTransitionLoadingState } from "../RoomTransitionLoadingState";
import { StartRoomTransitionFadeMessage } from "@messages/rendering/RoomTransitionMessages";
const START_POSITION = new Vector3(0, 0, 0).multiplyScalar(ROOT_SCALE);
const START_CAMERA = new Vector3(0, 1.7, 0).multiplyScalar(ROOT_SCALE);
const START_SCALE = SCALE_DEFAULT;
const tmpVector31 = new Vector3();
export class StartPointSystem extends RendererSystem {
    constructor(renderer) {
        super(renderer, "StartPointSystem");
        this.currentRoomModelUID = ROOT_MODEL_UID;
        this.rooms = new Map();
        this.onRoomChangedDelegate = new MulticastDelegate();
        this.onLayouterCreationFinishedDelegate = new MulticastDelegate1();
        this.connection = busService.connection("StartPointSystem");
        /// Message handlers ///
        this.handleRoomChangedMessage = (msg) => __awaiter(this, void 0, void 0, function* () {
            if (this.currentRoomModelUID === msg.uid)
                return;
            roomTransitionLoadingState.beginWaitingForPrewarm(msg.uid);
            const newRoomModel = modelPool.getModel(msg.uid);
            if (newRoomModel === undefined)
                return;
            this.currentRoomModelUID = newRoomModel.getUID();
            yield this.spawnUser();
            const room = this.getOrCreateRoom(msg.uid);
            this.processExitPoints(msg.uid, room);
            this.onRoomChangedDelegate.invoke();
        });
        this.handleLayoutCreationFinishedMessage = (msg) => {
            const room = this.getOrCreateRoom(msg.uid);
            room.isLayoutCreationFinished = true;
            this.onLayouterCreationFinishedDelegate.invoke(msg.uid);
        };
        this.handleScriptsCreationFinished = (msg) => {
            const room = this.getOrCreateRoom(msg.roomUid);
            room.isScriptsCreationFinished = true;
            this.onLayouterCreationFinishedDelegate.invoke(msg.roomUid);
        };
        this.handleRequestSpawnedPointsInRoomMessage = (msg, askId) => __awaiter(this, void 0, void 0, function* () {
            const points = [];
            const room = yield this.waitForRoom(msg.roomUid, !msg.filter.waitForScriptsCreationFinished);
            const filterPoints = this.filterPoints(room, {
                flags: msg.filter.flags,
                id: msg.filter.id
            });
            if (msg.filter.random !== undefined) {
                const randomData = yield this.getRandomSpawnPointByArray(filterPoints);
                if (randomData !== undefined) {
                    points.push({
                        uid: randomData.model.modelUID,
                        flags: randomData.facet.flags,
                        position: convertToNumber3(randomData.model.worldPosition),
                        orientation: convertToNumber4(randomData.model.worldOrientation),
                        scale: convertToNumber3(randomData.model.worldScale)
                    });
                }
            }
            else {
                for (const p of filterPoints) {
                    const modelInterface = p.model;
                    if (modelInterface.matrixWorldValid) {
                        points.push({
                            uid: modelInterface.modelUID,
                            flags: p.facet.flags,
                            position: convertToNumber3(modelInterface.worldPosition),
                            orientation: convertToNumber4(modelInterface.worldOrientation),
                            scale: convertToNumber3(modelInterface.worldScale)
                        });
                    }
                    else {
                        try {
                            const model = modelPool.getModel(modelInterface.modelUID);
                            if (model === undefined) {
                                console.error(`[model] No available start point for ${determineModelNameForPrinting(modelInterface.modelUID)}. This shouldn't happen.`);
                                continue;
                            }
                            yield new Promise((resolve) => {
                                const unsubscribe = model.setOnTransformChangedHandler((_model, transform) => {
                                    unsubscribe();
                                    resolve(transform);
                                });
                            });
                            points.push({
                                uid: modelInterface.modelUID,
                                flags: p.facet.flags,
                                position: convertToNumber3(model.getWorldPosition()),
                                orientation: convertToNumber4(model.getWorldOrientation()),
                                scale: convertToNumber3(model.getWorldScale())
                            });
                        }
                        catch (error) {
                            console.error("handleRequestSpawnedPointsInRoomMessage", error);
                        }
                    }
                }
            }
            this.connection.answer(askId, new SpawnPointsInRoomResponseMessage(points));
        });
        /// User helper methods ///
        this.spawnUser = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (this.currentRoomModelUID === ROOT_MODEL_UID) {
                yield this.waitForRoomChanged();
            }
            const currentRoomModelUID = this.currentRoomModelUID;
            const room = yield this.waitForRoom(currentRoomModelUID);
            camera.perspectiveCam.position.copy(START_CAMERA);
            localPlayerContainer.setScale(START_SCALE);
            const respawnDirection = this.respawnDirection !== undefined
                ? new Vector3().set(this.respawnDirection[0], this.respawnDirection[1], this.respawnDirection[2])
                : new Vector3();
            const respawnPosition = this.respawnPosition !== undefined
                ? new Vector3(this.respawnPosition[0], this.respawnPosition[1], this.respawnPosition[2])
                : START_POSITION;
            // apply the stored respawn position/view direction from before switching to VR-Mode, if it exists
            localPlayerContainer.setPosition(respawnPosition);
            localPlayerContainer.setPlatformYawAngle(Math.atan2(respawnDirection.x, respawnDirection.z) + degToRad(180));
            if (room !== undefined && this.respawnPosition === undefined) {
                const pointData = yield this.getRandomSpawnPoint(room);
                if (pointData !== undefined) {
                    const { spawnPosition, spawnOrientation } = this.applyRoomRotationToSpawnPoint(pointData.model.worldPosition, pointData.model.worldOrientation, pointData.model.modelUID);
                    localPlayerContainer.setPosition(spawnPosition);
                    localPlayerContainer.setOrientation(spawnOrientation);
                    this.connection.send(new SetPlayerWorldTransformMessage({ position: [spawnPosition.x, spawnPosition.y, spawnPosition.z] }));
                    tmpVector31.y = (_a = pointData.facet.params.defaultHeight) !== null && _a !== void 0 ? _a : 0;
                }
            }
            localPlayerContainer.updateTransform();
            this.connection.sendMultiple([new UserSpawnedMessage(currentRoomModelUID)]);
            this.respawnPosition = undefined;
            this.respawnDirection = undefined;
        });
        this.setRespawnPosition = (_isPresenting, position, direction) => {
            this.respawnPosition = position;
            this.respawnDirection = direction;
        };
        renderManager.isXRPresenting$.add(this.setRespawnPosition);
        renderManager.isXRPresenting$.add(() => { void this.spawnUser(); });
        this.connection.subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        this.connection.subscribeByType(StartRoomTransitionFadeMessage, (msg) => roomTransitionLoadingState.beginTransition(msg.uid));
        this.connection.subscribeByType(LayoutCreationFinishedMessage, this.handleLayoutCreationFinishedMessage);
        this.connection.subscribeByType(ScriptsCreationFinished, this.handleScriptsCreationFinished);
        this.connection.subscribeByType(SessionConnectMessage, () => { void this.spawnUser(); });
        this.connection.subscribeByType(UserSpawnMessage, this.spawnUser);
        this.connection.subscribeForAskRequest(SpawnPointsInRoomRequestMessage, this.handleRequestSpawnedPointsInRoomMessage);
    }
    register(point) {
        const model = modelPool.getModelOrThrow(point.owner.modelUID);
        const roomModel = model.getRoom();
        if (roomModel === undefined)
            return;
        const room = this.getOrCreateRoom(roomModel.getUID());
        room.points.set(model.getUID(), point);
        if (this.currentRoomModelUID === roomModel.getUID() && room.isLayoutCreationFinished && room.isScriptsCreationFinished) {
            this.processExitPoints(roomModel.getUID(), room);
        }
    }
    unregister(point) {
        const model = modelPool.getModelOrThrow(point.owner.modelUID);
        const roomModel = model.getRoom();
        if (roomModel === undefined)
            return;
        const room = this.rooms.get(roomModel.getUID());
        if (room === undefined)
            return;
        room.points.delete(model.getUID());
        if (room.points.size === 0) {
            this.rooms.delete(roomModel.getUID());
        }
    }
    /// Wait helper methods ///
    waitForRoomChanged() {
        return __awaiter(this, void 0, void 0, function* () {
            yield new Promise((resolve) => {
                const func = () => {
                    if (this.currentRoomModelUID === ROOT_MODEL_UID)
                        return;
                    this.onRoomChangedDelegate.remove(func);
                    resolve();
                };
                this.onRoomChangedDelegate.add(func);
            });
        });
    }
    waitForRoom(uid_1) {
        return __awaiter(this, arguments, void 0, function* (uid, ignoreScripts = false) {
            // Don't wait for root
            if (ROOT_MODEL_UID === uid)
                throw new Error("Can't wait for root room");
            const room = this.rooms.get(uid);
            if ((room === null || room === void 0 ? void 0 : room.isLayoutCreationFinished) === true && (ignoreScripts || room.isScriptsCreationFinished))
                return room;
            return yield new Promise((resolve) => {
                const func = (newRoomUid) => {
                    if (newRoomUid !== uid)
                        return;
                    const room = this.rooms.get(uid);
                    if (room === undefined)
                        return;
                    if (!room.isLayoutCreationFinished)
                        return;
                    if (!room.isScriptsCreationFinished)
                        return;
                    this.onLayouterCreationFinishedDelegate.remove(func);
                    resolve(room);
                };
                this.onLayouterCreationFinishedDelegate.add(func);
            });
        });
    }
    /// Room helper methods ///
    getOrCreateRoom(uid) {
        let room = this.rooms.get(uid);
        if (room === undefined) {
            room = {
                points: new Map(),
                isLayoutCreationFinished: false,
                isScriptsCreationFinished: false
            };
            this.rooms.set(uid, room);
        }
        return room;
    }
    /// Spawn point helper methods ///
    applyRoomRotationToSpawnPoint(staticPosition, staticOrientation, spawnModelUID) {
        // Walk up the model hierarchy from the spawn point to find an ancestor with RotatingRenderable.
        // This handles rooms whose rotation comes from a renderer-side RotatingFacet (purely visual rotation
        // not reflected in worker world transforms). For script-driven rotation via LocalTransformFacet,
        // the worker resets the room orientation to identity before spawning (see RoomManagerModule).
        let current = modelPool.getModel(spawnModelUID);
        while (current !== undefined && !current.isRoot()) {
            const modelInterface = this.renderer.getModel(current.getUID());
            if (modelInterface !== undefined) {
                const rotatingRenderable = modelInterface.getFacetRenderer(RotatingRenderable);
                if (rotatingRenderable !== undefined) {
                    const accumulatedLocal = rotatingRenderable.getAccumulatedRotation();
                    const rotatingModelOrientation = modelInterface.worldOrientation;
                    const accumulatedWorld = rotatingModelOrientation.clone()
                        .multiply(accumulatedLocal)
                        .multiply(rotatingModelOrientation.clone().invert());
                    const rotatingModelCenter = modelInterface.worldPosition;
                    const relativeToCenter = staticPosition.clone().sub(rotatingModelCenter);
                    relativeToCenter.applyQuaternion(accumulatedWorld);
                    const spawnPosition = relativeToCenter.add(rotatingModelCenter);
                    const spawnOrientation = accumulatedWorld.clone().multiply(staticOrientation);
                    return { spawnPosition, spawnOrientation };
                }
            }
            current = current.getParent();
        }
        return { spawnPosition: staticPosition, spawnOrientation: staticOrientation };
    }
    getRandomSpawnPoint(room) {
        return __awaiter(this, void 0, void 0, function* () {
            const points = this.filterPoints(room, { flags: StartPointFlags.SpawnPoint });
            return yield this.getRandomSpawnPointByArray(points);
        });
    }
    filterPoints(room, filter) {
        const filtered = [];
        for (const point of room.points.values()) {
            if (!point.currentFacet)
                continue;
            if (filter.flags && !(point.currentFacet.flags & filter.flags)) {
                continue;
            }
            if (filter.id && filter.id !== point.currentFacet.params.id) {
                continue;
            }
            filtered.push({
                facet: point.currentFacet,
                model: point.owner,
                instance: point
            });
        }
        return filtered;
    }
    getRandomSpawnPointByArray(points) {
        return __awaiter(this, void 0, void 0, function* () {
            if (points.length === 0) {
                return undefined;
            }
            const startPointIndex = Math.round((points.length - 1) * Math.random());
            const point = points[startPointIndex];
            if (point === undefined)
                return undefined;
            const modelInterface = point.model;
            const model = modelPool.getModel(modelInterface.modelUID);
            if (model === undefined)
                return undefined;
            if (!modelInterface.matrixWorldValid) {
                yield new Promise((resolve) => {
                    const unsubscribe = model.setOnTransformChangedHandler((model, _transform) => {
                        unsubscribe();
                        resolve(model.getWorldTransform());
                    });
                });
                return {
                    facet: point.facet,
                    model: modelInterface
                };
            }
            return {
                facet: point.facet,
                model: modelInterface
            };
        });
    }
    processExitPoints(roomUid, room) {
        // check in current room
        if (this.currentRoomModelUID !== roomUid)
            return;
        // create only in sub room
        const roomModel = modelPool.getModel(this.currentRoomModelUID);
        if (roomModel === undefined || roomModel.getRoomDepth() < 1)
            return;
        const exitPoints = this.filterPoints(room, {
            flags: StartPointFlags.ExitPoint
        });
        const spawnPoints = this.filterPoints(room, {
            flags: StartPointFlags.SpawnPoint
        });
        // In order to use the path as a name we need to remove all symbols reserved by paths (namely: [, ], /)
        const currentRoomPath = modelToAbsolutePath(roomModel)
            .replace(/[\[\]\/]/g, (c) => c === '[' ? '(' : c === ']' ? ')' : '|');
        if (exitPoints.length > 0) {
            // removed only spawn points
            for (const point of spawnPoints) {
                if (exitPoints.some(exitPoint => exitPoint.model.modelUID === point.model.modelUID))
                    continue;
                point.instance.deleteExitPointModel();
            }
            if (exitPoints.length === 1) {
                exitPoints[0].instance.createExitPointModel(currentRoomPath);
            }
            else {
                for (const [idx, point] of exitPoints.entries()) {
                    point.instance.createExitPointModel(`${currentRoomPath}_${idx}`);
                }
            }
        }
        else {
            if (spawnPoints.length === 0) {
                const roomModel = modelPool.getModel(roomUid);
                if (roomModel === undefined) {
                    console.log("[rendering] Room doesn't exist. Cannot create default exit point");
                }
                else {
                    console.warn(`[rendering] There is no spawn point in room "${roomModel.getName()}", therefore a default start point is generated`);
                    this.createDefaultStartPoint(roomModel);
                }
            }
            for (const [idx, point] of spawnPoints.entries()) {
                console.debug(`Creating exit point ${determineModelNameForPrinting(point.model.modelUID)}`);
                point.instance.createExitPointModel(`${currentRoomPath}_${idx}`);
            }
        }
    }
    createDefaultStartPoint(room) {
        const defaultExitPoint = modelPool.createModel("DefaultExitPoint", room);
        defaultExitPoint.setFacet(new StartPointFacet(StartPointFlags.ExitPoint | StartPointFlags.SpawnPoint));
        defaultExitPoint.setFacet(new LocalTransformFacet([0, 1, 0]));
        defaultExitPoint.flushMessages();
    }
}
